import _ from 'lodash';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import WeakList from 'relevant-shared/misc/weakList';
import { stores } from './index';

// All non garbage-collected query clients that have used fetchUsers()
const seenClients = new WeakList();

const fetchUsers = (queryClient) => async () => {
	seenClients.add(queryClient);
	await stores.users.loadAll();
	const users = stores.users.js() || [];
	return _.sortBy(users, 'firstname');
};

const invalidate = (queryClient) => queryClient.invalidateQueries({ queryKey: ['users'] });

stores.users.onSet.add(() => {
	seenClients.getAll().forEach(invalidate);
});

export const useUsers = () => {
	const queryClient = useQueryClient();
	const res = useQuery({
		queryKey: ['users'],
		queryFn: fetchUsers(queryClient),
		refetchOnMount: false,
		gcTime: Infinity,
	});
	if (res.isLoading) {
		return { ...res, data: [] };
	}
	return res;
};

export const useUserById = (id) => {
	const { data: users } = useUsers();
	return users ? _.find(users, { id }) : undefined;
};

export const useUsersByPublisherId = (publisherId) => {
	const { data: users } = useUsers();
	return users ? _.filter(users, { publisherId }) : [];
};

export const useAddUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (user) => stores.users.add(user),
		onSuccess: () => invalidate(queryClient),
	});
};

export const useEditUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (user) => stores.users.edit(user),
		onSuccess: () => invalidate(queryClient),
	});
};

export const useDeleteUser = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id) => stores.users.delete(id),
		onSuccess: () => invalidate(queryClient),
	});
};
